/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { nanoid } from "nanoid";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Form from "react-bootstrap/Form";
import { useDispatch, useSelector } from "react-redux";
import { paymentHandler } from "../../redux/handlers";

const inputStyles = {
  background: "#FFF",
  borderRadius: "8px",
  filter: "drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.14))",
};

const selectStyles = {
  ".MuiInputBase-input": {
    display: "flex",
    alignItems: "center",
    padding: "8px 14px",
    color: "#061126",
  },
  fieldset: {
    border: 0,
    background: "rgb(255, 255, 255)",
    borderRadius: "8px",
    filter: "drop-shadow(rgba(0, 0, 0, 0.14) 0px 2px 4px)",
    color: "#061126",
  },
  ".MuiSvgIcon-root": {
    zIndex: 9,
  },
  ".MuiSelect-select": {
    zIndex: 10,
    height: "32.4px",
  },
};

export default function PayCredit({
  payment,
  money,
  actions,
  order,
  ui,
  provider,
  settings,
  orderData,
  ...props
}) {
  const dispatch = useDispatch();

  const { selectedMethod } = payment;
  const { setAmount } = money;
  const { details: orderDetails } = order;
  const { setData: setProviderData, setRemark } = provider;
  const { paymentSettings } = settings;

  const bankCompany = useSelector((state) => state.payment.bankCompany);
  const paymentSetting = useSelector((state) => state.payment.paymentSetting);
  const billData = useSelector((state) => state.bill.data);

  const [bankList, setBankList] = useState([]);
  const [cardTypeList, setCardTypeList] = useState([]);
  const [bankSelect, setBankSelect] = useState(null);
  const [totalAmount, setTotalAmount] = useState(0);
  const [defaultFeePercentage, setDefaultFeePercentage] = useState(0);
  const [roundingType, setRoundingType] = useState(null);
  const [displayFee, setDisplayFee] = useState(0);
  const [displayVatOnFee, setDisplayVatOnFee] = useState(0);

  const orderAmount = useMemo(() => {
    return parseFloat(orderDetails?.paymentNet || 0);
  }, [orderDetails]);

  const remainingAmount = useMemo(() => {
    const netAmount = parseFloat(orderDetails?.paymentNet || 0);
    const totalPaid = parseFloat(orderDetails?.paymentTotal || 0);
    const remaining = Math.max(0, netAmount - totalPaid);
    const truncated = Math.floor(remaining * 100) / 100;
    let strValue = String(truncated);

    if (!strValue.includes(".")) {
      strValue += ".00";
    } else {
      const parts = strValue.split(".");

      if (parts[1].length === 1) {
        strValue += "0";
      }
    }

    return strValue;
  }, [orderDetails]);

  const [cardInfo, setCardInfo] = useState({
    type: null,
    bankType: null,
    bank: null,
    fee: 0,
    feeValue: null,
    last_digit: "",
    trace_number: "",
    remark: "",
  });

  const roundValue = useCallback(
    (value) => {
      if (!value) return 0;

      if (!roundingType) {
        return Math.floor(value * 100) / 100;
      }

      switch (roundingType) {
        case "ceil":
          return Math.ceil(value);
        case "floor":
          return Math.floor(value);
        case "round":
          return Math.round(value);
        default:
          return Math.floor(value * 100) / 100;
      }
    },
    [roundingType]
  );

  useEffect(() => {
    if (billData) {
      if (billData.rounding_type) {
        setRoundingType(billData.rounding_type);
      } else {
        setRoundingType(null);
      }
    }
  }, [billData]);

  useEffect(() => {
    const branchCreditSetting =
      paymentSetting?.branch?.branchCreditSetting?.[0];

    if (branchCreditSetting?.fee) {
      const fee = parseFloat(branchCreditSetting.fee);
      setDefaultFeePercentage(fee);
      setCardInfo((prev) => ({
        ...prev,
        feeValue: fee,
      }));
    }
  }, [paymentSetting]);

  useEffect(() => {
    if (typeof setRemark === "function") {
      setRemark("");
    }
  }, [setRemark]);

  useEffect(() => {
    const initialAmount = parseFloat(remainingAmount);
    setTotalAmount(initialAmount);
    calculateAmounts(initialAmount);
  }, [remainingAmount]);

  const formatToDisplay = useCallback((num) => {
    const truncated = Math.round(num * 100) / 100;

    let strValue = String(truncated);

    if (!strValue.includes(".")) {
      strValue += ".00";
    } else {
      const parts = strValue.split(".");

      if (parts[1].length === 1) {
        strValue += "0";
      }
    }

    return strValue;
  }, []);

  const calculateAmounts = useCallback(
    (initialAmount = null) => {
      const baseAmount = initialAmount !== null ? initialAmount : orderAmount;
      let cardFee = 0;
      let vatOnFee = 0;

      if (cardInfo.fee) {
        const feePercentage =
          cardInfo.feeValue !== null ? cardInfo.feeValue : defaultFeePercentage;

      cardFee = (baseAmount * parseFloat(feePercentage)) / 100;
        cardFee = Math.round(cardFee * 100) / 100;

        vatOnFee = cardFee * 0.07;
        vatOnFee = Math.round(vatOnFee * 100) / 100;

        setDisplayFee(cardFee);
        setDisplayVatOnFee(vatOnFee);
      }

      const totalWithFeesAndVat = baseAmount + cardFee + vatOnFee;
      setTotalAmount(formatToDisplay(totalWithFeesAndVat));
    },
    [cardInfo, orderAmount, defaultFeePercentage, formatToDisplay]
  );

  const handleCardTypeChange = useCallback((type) => {
    setCardInfo((prev) => ({
      ...prev,
      type,
    }));
  }, []);

  const handleBankTypeChange = useCallback((value) => {
    setCardInfo((prev) => ({
      ...prev,
      bankType: value,
    }));
  }, []);

  const handleBankChange = useCallback(
    (value) => {
      setCardInfo((prev) => ({
        ...prev,
        bank: value,
      }));

      if (Array.isArray(bankList) && bankList.length > 0) {
        const bank = bankList.find((item) => item.id === value);
        if (bank) {
          setBankSelect(bank);
        }
      }
    },
    [bankList]
  );

  const handleFeeChange = useCallback((value) => {
    setCardInfo((prev) => ({
      ...prev,
      fee: value,
    }));
  }, []);

  const handleLastDigitsChange = useCallback((e) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    setCardInfo((prev) => ({
      ...prev,
      last_digit: value,
    }));
  }, []);

  const handleTraceNumberChange = useCallback((e) => {
    setCardInfo((prev) => ({
      ...prev,
      trace_number: e.target.value,
    }));
  }, []);

  const handleRemarkChange = useCallback(
    (e) => {
      const newRemark = e.target.value;

      setCardInfo((prev) => ({
        ...prev,
        remark: newRemark,
      }));

      if (typeof setRemark === "function") {
        setRemark(newRemark);
      }
    },
    [setRemark]
  );

  const handleAddPayment = useCallback(() => {
    if (!bankSelect || !cardInfo.type) {
      alert("กรุณาระบุข้อมูลให้ครบถ้วน");
      return;
    }

    let fee = 0;
    if (cardInfo.fee) {
      fee = displayFee;

      fee = roundValue(fee);
    }

    let vatOnFee = displayVatOnFee;

    vatOnFee = roundValue(vatOnFee);

    const formatNumberNoRounding = (num) => {
      return Math.floor(num * 100) / 100;
    };

    const formatToString = (num) => {
      const truncated = formatNumberNoRounding(num);

      let strValue = String(truncated);

      if (!strValue.includes(".")) {
        strValue += ".00";
      } else {
        const parts = strValue.split(".");

        if (parts[1].length === 1) {
          strValue += "0";
        }
      }

      return strValue;
    };

    const paymentItem = {
      id: nanoid(),
      channel: selectedMethod,
      amount: totalAmount,
      provider: bankSelect,
      card_info: {
        ...cardInfo,
        type: cardInfo.type,
        credit_card_type_id: cardInfo.type?.toString() || "1",
        credit_card_last_four_digits: cardInfo.last_digit || "",
        trace_number: cardInfo.trace_number || "",
        remark: cardInfo.remark || "",
        vat_on_fee: formatToString(vatOnFee),
        rounding_type: roundingType,
      },
      remark: cardInfo.remark || "",
      status: 1,
    };

    if (fee > 0) {
      paymentItem.card_info.credit_fee = formatToString(fee);
      paymentItem.card_info.credit_fee_type = "percent";
    } else {
      paymentItem.card_info.credit_fee = "0.00";
      paymentItem.card_info.credit_fee_type = null;
    }

    if (typeof payment.setData === "function") {
      const currentPayments = Array.isArray(payment.data)
        ? [...payment.data]
        : [];

      payment.setData([...currentPayments, paymentItem]);
    }

    if (typeof setProviderData === "function") {
      setProviderData(bankSelect);
    }

    if (typeof setRemark === "function") {
      setRemark(cardInfo.remark || "");
    }

    setAmount("0");

    if (typeof payment.setSelectedMethod === "function") {
      payment.setSelectedMethod({ paymentChannelID: 1 });
    }
  }, [
    bankSelect,
    cardInfo,
    remainingAmount,
    selectedMethod,
    payment,
    setProviderData,
    setRemark,
    setAmount,
    roundValue,
    roundingType,
    displayFee,
    displayVatOnFee,
  ]);

  const getFeeTypeDisplay = useMemo(() => {
    return paymentSetting?.branch?.branchCreditSetting?.[0]?.fee_type ===
      "amount"
      ? "บาท"
      : "%";
  }, [paymentSetting]);

  useEffect(() => {
    calculateAmounts(parseFloat(remainingAmount));
  }, [
    calculateAmounts,
    remainingAmount,
    cardInfo.fee,
    cardInfo.feeValue,
    defaultFeePercentage,
  ]);

  useEffect(() => {
    dispatch(paymentHandler.getBankCompany());
  }, [dispatch]);

  useEffect(() => {
    if (Array.isArray(bankCompany) && bankCompany.length > 0) {
      setCardTypeList(bankCompany);
    }
  }, [bankCompany]);

  useEffect(() => {
    if (paymentSettings?.credit) {
      setBankList(paymentSettings.credit);
    }
  }, [paymentSettings]);

  const isZeroAmount = useMemo(() => {
    if (
      !totalAmount ||
      totalAmount === "" ||
      totalAmount === "0" ||
      totalAmount === "0.00"
    ) {
      return true;
    }
    const amount = parseFloat(totalAmount);
    return amount === 0 || isNaN(amount);
  }, [totalAmount]);

  const isFormValid = useMemo(() => {
    return !!bankSelect && !!cardInfo.type && !isZeroAmount;
  }, [bankSelect, cardInfo.type, isZeroAmount]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter") {
        if (!isFormValid) {
          e.preventDefault();
          if (isZeroAmount) {
            console.log("Cannot proceed with zero amount");
          }
        } else {
          e.preventDefault();
          handleAddPayment();
        }
      }
    },
    [isFormValid, handleAddPayment, isZeroAmount]
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  return (
    <div className="d-flex flex-column h-100">
      <div className="d-flex align-items-center text-[#061126] mb-1">
        <div className="me-auto">
          <span className="fs-18 fw-500">Order NO.</span>
          <span className="fs-14 fw-500 ms-2">
            {orderDetails?.orderNo || ""}
          </span>
        </div>
        <div className="fs-18 fw-bold text-[#2DC76D]">ชำระผ่านบัตรเครดิต</div>
      </div>

      <div className="row gx-2 mb-3">
        <div className="col-6">
          <div className="mb-1 text-[12px] text-[#5A5A5A]">
            กรุณาระบุประเภทบัตร
          </div>
          <div className="shadow p-2 rounded-lg h-[48px]">
            <div className="pt-1">
              <Form.Check
                inline
                label="บัตรเครดิต"
                name="type"
                type="radio"
                id="type2"
                className="form-check-st-5 text-[14px] tc-7 me-2"
                value={1}
                onChange={() => handleCardTypeChange(1)}
                checked={cardInfo.type === 1}
              />
              <Form.Check
                inline
                label="บัตรเดบิต"
                name="type"
                type="radio"
                id="type3"
                className="form-check-st-5 text-[14px] tc-7 me-0"
                value={2}
                onChange={() => handleCardTypeChange(2)}
                checked={cardInfo.type === 2}
              />
            </div>
          </div>
        </div>
        <div className="col-6">
          <div className="fs-12 text-[#5A5A5A] mb-1">กรุณาระบุประเภทบัตร</div>
          <FormControl fullWidth>
            <Select
              value={cardInfo.bankType || ""}
              onChange={(e) => handleBankTypeChange(e.target.value)}
              sx={selectStyles}
            >
              {Array.isArray(cardTypeList) &&
                cardTypeList.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    <ListItemIcon>
                      <img
                        src={item.image_path}
                        className="h-[31px] w-[31px] object-cover"
                        alt={item.name}
                      />
                    </ListItemIcon>
                    <ListItemText primary={item.name} />
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </div>
      </div>

      <div className="mb-3">
        <div className="fs-12 text-[#5A5A5A] mb-1">กรุณาระบุธนาคาร</div>
        <FormControl fullWidth>
          <Select
            value={cardInfo.bank || ""}
            onChange={(e) => handleBankChange(e.target.value)}
            sx={selectStyles}
          >
            {Array.isArray(bankList) &&
              bankList.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  <ListItemIcon>
                    <img src={item.logo} className="w-[28px]" alt={item.name} />
                  </ListItemIcon>
                  <ListItemText primary={item.name} />
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </div>

      <div className="fs-12 text-[#5A5A5A]">รูปแบบค่าธรรมเนียม</div>
      <div className="">
        <Form.Check
          inline
          label="ไม่มีค่าธรรมเนียม"
          name="fee"
          type="radio"
          id="fee2"
          className="form-check-st-5 text-[14px] tc-7 me-2"
          value={0}
          onChange={() => handleFeeChange(0)}
          checked={cardInfo.fee === 0}
        />
        <Form.Check
          inline
          label="มีค่าธรรมเนียม"
          name="fee"
          type="radio"
          id="fee3"
          className="form-check-st-5 text-[14px] tc-7 me-0"
          value={1}
          onChange={() => handleFeeChange(1)}
          checked={cardInfo.fee === 1}
        />

        <span className="text-[#2DC76D] font-semibold text-[24px] ml-3">
          {defaultFeePercentage} {getFeeTypeDisplay}
        </span>
      </div>

      <div className="row mb-4 mt-3">
        <div className="col-6">
          <div className="fs-12 text-[#5A5A5A] mb-1">รหัสบัตร 4 ตัวท้าย</div>
          <input
            type="text"
            maxLength="4"
            className="form-control form-st-4 border-0 text-start pl-4 !text-[18px] fw-500"
            style={inputStyles}
            onChange={handleLastDigitsChange}
            value={cardInfo.last_digit}
          />
        </div>
        <div className="col-6">
          <div className="fs-12 text-[#5A5A5A] mb-1">หมายเลข Trace</div>
          <input
            type="text"
            className="form-control form-st-4 border-0 text-start pl-4 !text-[18px] fw-500"
            style={inputStyles}
            onChange={handleTraceNumberChange}
            value={cardInfo.trace_number}
          />
        </div>
      </div>

      <div className="mb-3">
        <div className="fs-12 fw-500 text-[#061126] mb-1">Remark</div>
        <textarea
          onChange={handleRemarkChange}
          className="form-control border-0"
          style={inputStyles}
          value={cardInfo.remark || ""}
        ></textarea>
      </div>

      <div className="mb-3">
        <div className="fs-12 mb-1 text-[#5A5A5A]">ยอดเงินที่ต้องรับชำระ</div>
        <input
          className="form-control text-[#FF0000] border-0 text-end fs-24 fw-500"
          style={{
            ...inputStyles,
            background: "#FFE6E6",
          }}
          disabled
          value={totalAmount}
        />
      </div>

      <div className="d-flex align-items-center justify-content-start pt-2">
        <button
          className="btn btn-st-q1 px-5"
          onClick={handleAddPayment}
          disabled={!isFormValid}
        >
          Enter
        </button>
      </div>
    </div>
  );
}
